import React from "react";

import {
  Navigate,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";

import Typography from "@mui/material/Typography";

import CalendarTakeover from "@app/analytics/Calendar/CalendarTakeover";
import { DesktopOnlyActionBar } from "@app/analytics/components/ActionBar";
import useEventGroups from "@app/analytics/hooks/calendar/useEventGroups";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import * as Events from "@app/analytics/tracking/events";
import ROUTES from "@app/common/routes";

import LoadSpinner from "@rui/LoadSpinner";
import Stack, { DesktopOnlyRow } from "@rui/Stack";
import Tab from "@rui/Tab";
import Tabs, { TabsContainer } from "@rui/Tabs";

import CalendarAddRead from "./components/CalendarAddRead";
import ConnectedCalendars from "./components/ConnectedCalendars";
import CopySchedulingLink from "./CopySchedulingLink";
import Scheduling from "./Scheduling";
import { StickyCalendarHeader } from "./styles";
import UpcomingEvents from "./UpcomingEvents";

const TABS = [
  {
    label: "Upcoming",
    path: ROUTES.CALENDAR_UPCOMING,
  },
  {
    label: "Scheduling",
    path: ROUTES.CALENDAR_SCHEDULING,
  },
];

const Calendar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { userMetadata } = useUserSettings();
  const {
    hasConnectedCalendarProviders,
    isLoading: isProvidersLoading,
    connectedCalendarProviders,
  } = useConnectedProviders();
  const { isLoading: isEventsLoading } = useEventGroups();

  const isLoading = isProvidersLoading || isEventsLoading;

  const handleTabChange = (event: React.SyntheticEvent, path: string) => {
    navigate(path);
  };

  const handleCopy = () => {
    Events.copySchedulingLink({ source: "calendar" });
    if (!userMetadata?.smartSchedulerTutorialComplete) {
      navigate({ search: "popup=rss_tutorial" }, { replace: true });
    }
  };

  return (
    <Stack
      sx={{ width: "100%", height: "100%", bgcolor: "#FFF" }}
      data-test-id="calendar-container"
    >
      <StickyCalendarHeader sx={{ bgcolor: "#FFF" }}>
        <DesktopOnlyActionBar>
          <Typography variant="h2">Calendar</Typography>
        </DesktopOnlyActionBar>
        <TabsContainer sx={{ marginTop: 1 }}>
          <Tabs value={location.pathname} onChange={handleTabChange}>
            {TABS.map((tab) => (
              <Tab key={tab.path} label={tab.label} value={tab.path} />
            ))}
          </Tabs>
          <DesktopOnlyRow
            variant="right"
            spacing={1}
            sx={{
              position: "absolute",
              right: 24,
              bottom: 6,
            }}
          >
            <ConnectedCalendars
              connectedCalendarProviders={connectedCalendarProviders}
            />
            <CalendarAddRead />
            <CopySchedulingLink onCopy={handleCopy} />
          </DesktopOnlyRow>
        </TabsContainer>
      </StickyCalendarHeader>
      {isLoading ? (
        <Stack sx={{ height: "100%", width: "100%" }} variant="center">
          <LoadSpinner />
        </Stack>
      ) : hasConnectedCalendarProviders ? (
        <Routes>
          <Route index element={<UpcomingEvents />} />
          <Route path="scheduling" element={<Scheduling />} />
          <Route
            path="*"
            element={<Navigate to={ROUTES.CALENDAR_UPCOMING} />}
          />
        </Routes>
      ) : (
        <CalendarTakeover />
      )}
    </Stack>
  );
};

export default Calendar;
